/**
 * daySupply.ts
 *
 * Pulls a day-supply count out of a prescription's directions, when the
 * pharmacy's own catalogue sig already states one.
 *
 * Two shapes appear in the Partell catalogue, both plain enough to be
 * unambiguous:
 *
 *   "Inj 0.5mL SQ 2x Weekly for 60 days"        → 60
 *   "Inject 0.1ml IM Twice Weekly (70 Days Supply)" → 70
 *
 * Matched case-insensitively, "day" or "days", with or without the
 * parentheses. A sig with neither pattern (most of them — dosing described as
 * "as directed" or a titration schedule with no fixed endpoint) yields no
 * number, and the caller decides what a missing day supply defaults to.
 */

const FOR_N_DAYS = /\bfor\s+(\d+)\s*days?\b/i;
const N_DAYS_SUPPLY = /\b(\d+)\s*days?\s*supply\b/i;

/** The day count a sig states outright, or null when it does not say. */
export function parseDaySupply(sig: string | null | undefined): number | null {
  const s = String(sig ?? '');
  const m = FOR_N_DAYS.exec(s) ?? N_DAYS_SUPPLY.exec(s);
  return m ? Number(m[1]) : null;
}

/**
 * The value to seed the Day supply field with: the sig's own number when it
 * has one, else `1` — the smallest real day supply, so the field always
 * starts on a value staff could plausibly submit as-is rather than one that
 * visibly needs fixing first. (Previously defaulted to `0.1`, deliberately
 * not a whole day so it read as an obvious placeholder — Adan: "not even 1
 * day supply? this will be confusing", so a fractional placeholder confused
 * more than it clarified.)
 */
export function defaultDaySupply(sig: string | null | undefined): string {
  const n = parseDaySupply(sig);
  return n !== null ? String(n) : '1';
}
